
import WDElement from '../wd-element';
import WDButton from '../button/wd-button';

export class WDListItem extends WDElement
{

	#prefix = null;
	#suffix = null;
	#list = null;
	#content = null;
	#model = null;
	#checkbox = null;
	#icon = null;
	#disclosure = null;
	#selected = false;
	#buttons = [];
	#clickable = false;

	constructor(listItem)
	{
		super().classList.add('wd-list-item');
		return this;
	};

	connectedCallback()
	{
		super.connectedCallback();
		
		if (this.list.focusable) this.addFocusEvents();
	}

	addFocusEvents()
	{
		this.tabIndex = '-1';
		
		// Keyboard navigation
		this.addEventListener('keydown', (e) =>
		{
			if (e.key === 'Enter') 
			{
				e.preventDefault();	//stops the keyboard from beeping on enter
				this.selected = true;
			}
		});
		
	};

	get list()
	{
		if (!this.#list) this.#list = this.closest('.wd-list');
		return this.#list;
	};

	on(evtName, func)
	{
		if (evtName == 'click' && this.#clickable == false)
		{
			this.setupClickable();
		}

		return super.on(evtName, func);
	};

	setupClickable()
	{
		this.#clickable = true;
		this.classList.add('is-clickable');

		super.on('click', evt =>
		{
			const elem = evt.srcElement;
			const tagName = elem.tagName.toLowerCase();

			//don't handle click actions on elements w/n the row that will be interacted with
			if (tagName == 'input' || tagName == 'button' || elem.parentElement.tagName.toLowerCase() == 'button')
			{
				evt.stopPropagation();
				evt.stopImmediatePropagation();
			}
			else
			{
				//mark the row as selected
				this.selected = true;
			}

		});

	};

	get content()
	{
		if (!this.#content)
		{
			this.content = wdc('<div/>');
		}

		return this.#content;
	};

	/**
	 * this allows us to use another element as the content wrapper, like
	 * in the case where we embed a form within a list row in wd-list-form-item
	 */
	set content(elem)
	{
		this.#content = elem;
		this.#content.classList.add('wd-list-item-content');
		this.append(this.#content);
	}

	get prefix()
	{
		if (!this.#prefix)
		{
			this.#prefix = wdc('<div class="wd-list-item-prefix"/>');
			this.content.before(this.#prefix);
		}

		return this.#prefix;
	};

	get suffix()
	{
		if (!this.#suffix)
		{
			this.#suffix = wdc('<div class="wd-list-item-suffix"/>');
			this.content.after(this.#suffix);
		}

		return this.#suffix;
	};

	add(content)
	{
		const cell = wdc('<div class="wd-list-item-cell"/>');

		if (content === undefined || content === null) content = '';
		cell.append(content);

		this.content.append(cell);

		return cell;
	};

	get model() { return this.#model; }
	set model(val) { 
		this.#model = val; 
		this.dataset.value = this.value;
	};

	//changing terminology to "model" instead of "data"
	get data() { return this.#model; };
	set data(val) { this.model = val; };

	get value()
	{
		let vf = this.list.valueField;

		//a little hack.  In a lot of cases valueField wasn't set when calling the list,
		//and there's no 'value' property in the data.  In that case, override valueField
		//with 'id' (because it's used a lot of the time)
		if (vf == 'value' && this.#model?.value === undefined && this.#model?.id !== undefined)
		{
			this.list.valueField = 'id';	//stops us from having to do this again
			vf = 'id';

			//console.error(`valueField ${vf} not set in data, trying 'id' instead`);
		}

		return this.#model[vf] || undefined;
	};


	get checkbox()
	{
		if (!this.#checkbox)
		{
			this.#checkbox = wdc('<input type="checkbox" name="list_item[]"/>');
			this.#checkbox.setAttribute('value', this.value);

			this.#checkbox.addEventListener('change', (evt) =>
			{
				const evtName = (this.#checkbox.checked == true) ? 'item:checked' : 'item:unchecked';
				this.trigger(evtName, this);
			});

			const cell = wdc('<div class="wd-list-item-checkbox"/>');
			cell.append(this.#checkbox);
			this.prefix.prepend(cell);

			this.on('item:checked item:unchecked', () => this.handleChecked());
		}

		return this.#checkbox;
	};

	set checkbox(val)
	{
		if (val === true) this.checkbox;
		else if (val === false && this.#checkbox) 
		{
			this.#checkbox.remove();
			this.#checkbox = null;
		}
	};

	get checked() { 
		return (this.#checkbox) ? this.classList.contains('is-checked') : false; 
	};

	set checked(val)
	{
		if (this.#checkbox.disabled == true) return this;

		this.#checkbox.checked = val;
		const evtName = (val === true) ? 'item:checked' : 'item:unchecked';

		this.trigger(evtName, this);
	};

	handleChecked()
	{
		if (this.#checkbox.checked == true) this.classList.add('is-checked');
		else this.classList.remove('is-checked');
	};

	//adds a button onto the row
	addButton(cfg)
	{
		const btn = new WDButton(cfg);
		this.suffix.append(btn);

		this.#buttons.push(btn);

		return btn;
	};

	get buttons() { return this.#buttons; };

	/**
		icon, thumbnail, buttons, disclosure
		- should we just have a prefix/suffix?
		- may end up being more code having to write the the icon or disclosure
		- what about thumbnails?  are they used enough to keep?  (or call them image?)
		*/

	get icon()
	{
		if (!this.#icon)
		{
			this.#icon = wdc('<span/>');
			const cont = wdc('<div class="wd-list-item-icon"/>').append(this.#icon);
			this.prefix.append(this.#icon);
		}
		return this.#icon;
	};

	set icon(iconParam)
	{
		//given a string template - use data property for the icon
		if (iconParam.includes('${'))
		{
			//setup a fallback and then try to interpolate if we have data
			let iconstr = 'fa-solid fa-icons';
			if (this.#model) iconstr = iconParam.interpolate(this.#model);

			this.icon.setAttribute('class', iconstr);
		}
		else
		{
			const icons = Array.isArray(iconParam) ? iconParam : iconParam.split(' ');
			this.icon.setAttribute('class', '');
			this.icon.classList.add(...icons);
		}
	};

	get disclosure()
	{
		if (!this.#disclosure)
		{
			//allow icon override
			this.#disclosure = new WDButton({ theme: 'clear', icon: 'fa-solid fa-chevron-right' });

			const wrapper = wdc('<div class="wd-list-item-disclosure"/>');
			wrapper.append(this.#disclosure);

			this.suffix.append(wrapper);
		}
		return this.#disclosure;
	};

	set disclosure(icon)
	{
		this.disclosure.icon = typeof (icon) == 'string' ? icon : 'fa-solid fa-chevron-right';
	};

	get selected() { return this.#selected; };
	set selected(val)
	{
		//this eneds to go first so deselectAll fires first
		const evtName = (val === true) ? 'item:selected' : 'item:unselected';
		this.trigger(evtName, this);

		this.#selected = val;

		if (val === true) this.classList.add('is-selected');
		else this.classList.remove('is-selected');	
	};

	get index() { return this.list.itemArray.indexOf(this); };

	get nextItem()
	{
		const idx = this.index;
		return (idx < this.list.items.length - 1) ? this.list.items[idx + 1] : null;
	};

	get prevItem()
	{
		const idx = this.index;
		return (idx > 0) ? this.list.items[idx - 1] : null;
	};

	removeItem()
	{
		this.list.removeItemAt(this.index);
	};

	insertAfterItem()
	{
		return this.list.insert(this.index + 1);
	};

	insertBeforeItem()
	{
		return this.list.insert(this.index - 1);
	};

	scrollTo(opts)
	{
		if (!this.list) return;

		//if it's not in view, scroll to it
		if (this.offsetTop > this.list.wrapper.clientHeight)
		{
			this.list.wrapper.scrollTop = this.offsetTop;
		}

	};

};

customElements.define('wd-list-item', WDListItem);

export class WDListHeader extends WDListItem
{

	#checkbox = null;

	constructor()
	{
		super().classList.add('wd-list-header');
	};

	set checkbox(val)
	{
		if (val === true) this.checkbox;
		else if (val === false && this.#checkbox) 
		{
			this.#checkbox.remove();
			this.#checkbox = null;
		}
	};

	get checkbox()
	{
		if (!this.#checkbox)
		{
			this.#checkbox = wdc('<span class="fa-solid fa-check"/>');

			const cell = wdc('<div class="wd-list-item-checkbox"/>');
			cell.append(this.#checkbox);
			cell.addEventListener("click", () => this.checkAll());

			this.prefix.prepend(cell);

		}

		return this.#checkbox;
	};

	get disclosure()
	{
		super.disclosure.style.visibility = 'hidden';
	};

	addButton(cfg)
	{
		const btn = super.addButton(cfg);
		btn.style.visibility = 'hidden';

		return btn;
	};

	get icon() { return super.icon; };
	set icon(icon)
	{
		super.icon = icon;
		super.icon.style.visibility = 'hidden';
	};

	checkAll()
	{
		const items = this.list.itemArray;

		if (items.length > 0)
		{
			const checkMode = (items[0].checked == true) ? false : true;
			items.forEach(item => item.checked = checkMode);
		}

		this.list.trigger('change');
	};

	get checked() { return false; };
	get selected() { return false; };

};

customElements.define('wd-list-header', WDListHeader);

export class WDListFooter extends WDListItem
{
	#checkbox = null;

	constructor()
	{
		super().classList.add('wd-list-footer');
	};

	get checked() { return false; };
	get selected() { return false; };
};

customElements.define('wd-list-footer', WDListFooter);
